<template>
  <div>
    <div class="modal-header p-4 is-flex is-justify-content-space-between is-align-items-center is-flex-wrap-wrap">
      <div v-if="currentTransaction.client" class="is-flex is-flex-direction-column">
        <b-tag rounded icon="user" size="is-small" type="is-primary is-light">
          {{ currentTransaction.client.name }}
          {{ currentTransaction.client.lastName }}
          <span>{{ formatRut(currentTransaction.clientIdNumber) }}</span>
        </b-tag>
        <div>
          <span v-if="currentTransaction.client" class="is-size-7">Id {{ $t('generalWords.transaction') }}: <span class="_id">{{ currentTransaction._id }}</span></span>
        </div>
      </div>
      <div class="is-flex is-flex-direction-column">
        <div class="is-flex is-flex-wrap-wrap">
          <div class="mr-2">
            <b-icon icon="calendar-alt" type="is-primary" /><span class="has-text-weight-semibold is-size-7">{{ formatDate(currentTransaction.createdAt) }}</span>
          </div>
          <div>
            <b-icon icon="paper-plane" type="is-primary" /><span class="has-text-weight-semibold is-size-7">{{ providerDictionary[currentTransaction.provider] }}</span>
          </div>
        </div>
      </div>
    </div>
    <div class="modal-card-body">
      <section>
        <div class="columns is-desktop is-variable is-3">
          <div class="column">
            <div
              v-if="currentTransaction.client"
              id="transaction-metadata"
              class="validation-module card-item secondary">
              <div class="modal-transaction-metadata">
                <div class="transaction-metadata-amounts">
                  <RelatedTransaction
                    :transaction="currentTransaction"
                    :payment-providers="paymentProviders"
                    :display-cancel-button="displayCancelButton" />
                  <div
                    v-if="$i18n.locale==='cl'"
                    class="is-flex is-justify-content-flex-end is-size-7 mx-2">
                    Valor UF: <span class="has-text-weight-semibold">$ {{ formatNumber(currentTransaction.secondaryCurrencyFactor) }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="column is-two-thirds-desktop is-full">
            <div
              v-if="debtDateToggle && ['cl'].includes($i18n.locale)"
              id="change-debt-date"
              class="validation-module">
              <DebtByDate
                :transaction="currentTransaction"
                @updated-transaction="$emit('update-transaction', { transaction: $event, index: currentTransactionIndex })"
                @is-loading="isButtonDisabled = $event" />
            </div>
            <div id="bank-statement-details" class="validation-module">
              <bank-statement-details
                v-model="selectedBankEntries"
                :id-prefix="currentTransactionId"
                :current-transaction-amount="currentTransaction.amount"
                type="checkbox"
                :default-search="currentTransaction.trustedIdNumbers.map(trustedId => trustedId.idNumber)"
                :bank-entries="bankEntries"
                :totals="true" />
            </div>
            <div
              v-if="['cl'].includes($i18n.locale)"
              id="create-wallet-entry"
              class="validation-module">
              <create-wallet-entry
                :transaction="currentTransaction" />
            </div>
            <div
              v-if="suggestibleTrustedIdNumbers.length && ['cl'].includes($i18n.locale)"
              id="trusted-id-numbers"
              class="validation-module">
              <div class="validation-card card-item secondary">
                <h5 class="is-size-5">Agregar ruts de confianza</h5>
                <div v-for="suggestible in suggestibleTrustedIdNumbers" :key="suggestible.idNumber" class="bank-entries">
                  <input
                    :id="`suggestible-${suggestible.idNumber}`"
                    v-model="suggestTrustedIdNumbers"
                    type="checkbox"
                    :value="suggestible">
                  <label :for="`suggestible-${suggestible.idNumber}`" class="pl-2">{{ suggestible.idNumber }}</label>
                </div>
              </div>
            </div>
            <div
              v-if="currentTransaction.status === 'PENDING'"
              id="provider-selection"
              class="validation-module">
              <div class="validation-card card-item secondary">
                <h5 class="is-size-5">{{ `${$t('generalWords.Select')} ${$t('generalWords.Provider')}` }}</h5>
                <b-select id="provider-select" v-model="provider" rounded size="is-small">
                  <option
                    v-for="providerOption in providerOptions"
                    :key="`option-${providerOption}`"
                    :value="providerOption">
                    {{ providerDictionary[providerOption] }}
                  </option>
                </b-select>
              </div>
            </div>
            <div id="add-validation-comment" class="validation-module">
              <div class="validation-card card-item secondary">
                <h5 class="is-size-5">{{ $t('generalWords.Comment') }}</h5>
                <div class="control">
                  <input
                    id="text-input-comment"
                    v-model="comment"
                    type="text"
                    class="input is-small input-comments">
                </div>
              </div>
            </div>
            <div class="transaction-buttons has-text-right">
              <button
                class="button is-button-secondary is-rounded mr-5"
                @click="$emit('is-single-process-open', false)">
                {{ $t('generalWords.GoBack') }}
              </button>
              <button
                id="button-process"
                class="button is-button-primary is-rounded"
                :disabled="!checkUserFeature('element.transaction-approval.button.process')"
                @click="processTransaction(currentTransactionId)">
                {{ $t('generalWords.Process') }}
              </button>
            </div>
          </div>
        </div>
      </section>
      <p v-if="errorText" class="help is-danger"> {{ errorText }} </p>
    </div>
  </div>
</template>

<script>
import { processSingleTransaction } from '@/application/services/api-clients/transactions';
import { dateToFormatedString } from '@/infrastructure/date';
import { formatNumber } from '@/infrastructure/format';
import BankStatementDetails from '@/ui/components/internal/transaction-approval/BankStatementDetails.vue';
import DebtByDate from '@/ui/components/internal/transaction-approval/DebtByDate.vue';
import CreateWalletEntry from '@/ui/components/internal/transaction-approval/CreateWalletEntry.vue'; // Test Iván Bustos
import RelatedTransaction from '@/ui/components/internal/pay-here/RelatedTransaction.vue';
import { setOrUnsetClientTrustedIdNumbers } from '@/application/services/api-clients/clients';
import userFeatures from '@/ui/mixins/user-features';

export default {
  components: { BankStatementDetails, RelatedTransaction, DebtByDate, CreateWalletEntry },
  mixins: [userFeatures],
  props: {
    bankEntries: { type: Array, default: () => [] },
    confirmableTransactions: { type: Array, default: () => [] },
    currentTransaction: { type: Object, default: () => ({}) },
    currentTransactionId: { type: String, default: '' },
    currentTransactionIndex: { type: Number, default: -1 },
    debtDateToggle: { type: Boolean, default: false },
    paymentProviders: { type: Array, default: () => [] },
    providerDictionary: { type: Object, default: () => ({}) }
  },
  data() {
    return {
      comment: '',
      displayCancelButton: 'hide',
      errorText: '',
      errorDictionary: {
        errorRegisteringTransaction: {
          br: 'Ocorreu um erro ao tentar processar a transação',
          cl: 'Ha ocurrido un error al tratar de procesar la transacción',
          pe: 'Ha ocurrido un error al tratar de procesar la transacción'
        }[this.$i18n.locale],
        errorUpdatingTrustedIdNumbers: {
          br: '',
          cl: 'Ha ocurrido un error al tratar de procesar los ruts sugeridos como ruts de confianza para el cliente',
          pe: 'Ha ocurrido un error al tratar de procesar los ruts sugeridos como ruts de confianza para el cliente'
        }[this.$i18n.locale]
      },
      provider: '',
      providerOptions: {
        br: ['SANTANDER_MANUAL', 'ITAU_MANUAL'],
        cl: ['WIRE_TRANSFER', 'CASH_DEPOSIT'],
        pe: []
      }[this.$i18n.locale],
      selectedBankEntries: { totalAmount: 0, bankEntriesIds: [], bankEntriesEntities: [] },
      suggestTrustedIdNumbers: []
    };
  },
  computed: {
    suggestibleTrustedIdNumbers() {
      const trustedIdNumbersMap = this.currentTransaction.trustedIdNumbers.map(trustedIdNumber => trustedIdNumber.idNumber);
      const onlyUnique = (value, index, self) => self.indexOf(value) === index;
      return this.selectedBankEntries.bankEntriesEntities.reduce((acc, selectedEntity, index, self) => {
        const notInTrustedIdNumbers = !trustedIdNumbersMap.includes(selectedEntity.idNumber);
        const unique = onlyUnique(selectedEntity, index, self);
        if (notInTrustedIdNumbers && unique) {
          acc.push({ idNumber: selectedEntity.idNumber, status: 'SUGGESTED', name: selectedEntity.name });
        }
        return acc;
      }, []);
    }
  },
  methods: {
    formatDate: (date, format = 'DD/MM/YYYY HH:mm', inputFormat = '') => dateToFormatedString(date, format, inputFormat),
    formatNumber: number => formatNumber({ number }),
    formatRut(rut) {
      const rutParts = rut.split('-');
      return `${this.formatNumber(Number(rutParts[0]))}-${rutParts[1]}`;
    },
    alert({ message, alertType }) {
      this.$buefy.notification.open({
        duration: 3000,
        message,
        type: alertType,
        hasIcon: true
      });
    },
    async processTransaction(transactionId) {
      if (this.currentTransaction.status === 'PENDING' && !this.provider) {
        this.errorText = {
          br: 'Você deve escolher um método de pagamento',
          cl: 'Se debe elegir método de pago',
          pe: 'Se debe elegir método de pago'
        }[this.$i18n.locale];
        return;
      }
      this.isButtonDisabled = true;
      this.errorText = '';
      let trustedIdNumbersWereAdded = false;
      if (this.suggestTrustedIdNumbers.length) {
        try {
          trustedIdNumbersWereAdded = !!await setOrUnsetClientTrustedIdNumbers(this.currentTransaction.clientId, this.suggestTrustedIdNumbers);
        } catch (_error) {
          this.alert({ message: `${this.errorDictionary.errorUpdatingTrustedIdNumbers}`, alertType: 'is-warning' });
        }
      }
      const { totalAmount, bankEntriesIds } = this.selectedBankEntries;
      try {
        await processSingleTransaction(
          transactionId,
          totalAmount,
          this.comment,
          bankEntriesIds,
          trustedIdNumbersWereAdded,
          this.provider
        );
        const message = {
          br: `A transação id ${transactionId} foi registrada`,
          cl: `Se registró la transacción ${transactionId}`,
          pe: `Se registró la transacción ${transactionId}`
        }[this.$i18n.locale];
        this.alert({ message, alertType: 'is-info' });
      } catch (_error) {
        this.alert({ message: `${this.errorDictionary.errorRegisteringTransaction} ${transactionId}`, alertType: 'is-warning' });
      }
      this.isButtonDisabled = false;
      this.$emit('is-single-process-open', false);
      await this.$emit('load-transactions');
    }
  }
};

</script>

<style scoped>
.modal-header {
  background-color: white;
  border-bottom: solid 1px rgb(237, 237, 237);
}
.modal-title {
  margin: 0rem;
}
.validation-card {
  display: flex;
  flex-flow: row wrap;
  align-items: center;
  gap: 1rem;
  padding: 1.5rem;
}
.modal-card-body{
  background-color: #F9F9F9;
}
.modal-card-body .validation-totals {
  display: flex;
  flex-flow: column wrap;
  align-items: center;
  margin-top: 1rem;
  font-weight: bold;
}
.validation-module {
  padding: 0rem 0rem 1rem 0rem;
}
.transaction-metadata-attributes {
  gap: 1.5rem;
}
  div._id {
    font-size: 0.65rem;
  }
  .transaction-buttons {
    margin-top: 15px;
  }
  .close-modal {
    position: absolute;
    right: 20px;
    top: 20px;
    width: 40px;
  }
  .modal-content p {
    background-color: #F9F9F9;
    margin-bottom: 15px;
  }
  .input-comments {
    width: 220px;
    border-radius: 624.938rem;
    border: 1px solid var(--color-silver) !important;
  }
  .input-comments:focus {
    border: 1px solid var(--primary) !important;
  }
</style>
